import { Text } from '@clay-run/glaze/text';
import { ChartLine } from '@phosphor-icons/react/dist/icons/ChartLine';
import { useCampaignAsk } from '../api-client';
import { PLATFORM_SPECS } from '../platforms';
import type { AdPlatform } from '../types';
import { ChatThread, useChatSession } from './ChatThread';

const STARTERS = ['How is this campaign doing?', 'Why did cost per result change?', 'Should we dial up or down?'];

/** Read-only analyst for one campaign. It answers from the platform's live numbers and can't change anything. */
export function CampaignAsk({ platform, campaignId }: { platform: AdPlatform; campaignId: string }) {
  const ask = useCampaignAsk(platform, campaignId);
  const chat = useChatSession({
    opening: [
      {
        id: 0,
        role: 'assistant',
        text: `Ask me about this campaign. I read its live ${PLATFORM_SPECS[platform].label} numbers and compare them with Growth's benchmarks. I can recommend changes, but I can't make them.`,
        choices: STARTERS,
      },
    ],
    isPending: ask.isPending,
    request: (messages, onReply) => ask.mutate(messages, { onSuccess: reply => onReply(reply, null) }),
  });

  return (
    <section
      aria-label="Ask about this campaign"
      className="flex h-[520px] flex-col overflow-hidden rounded-2xl border border-border-primary bg-bg-primary"
    >
      <header className="flex items-center gap-2.5 border-b border-border-primary px-4 py-3">
        <span className="flex size-7 items-center justify-center rounded-full bg-bg-inverse text-content-inverse">
          <ChartLine size={14} weight="bold" />
        </span>
        <Text weight="semibold">Ask about this campaign</Text>
      </header>
      <ChatThread
        messages={chat.messages}
        isPending={ask.isPending}
        error={ask.error}
        onSend={chat.send}
        onRetry={chat.retry}
        inputId="adcc-campaign-ask"
        placeholder="Ask about this campaign"
        pendingLabel="Looking at the numbers…"
      />
    </section>
  );
}
